import React, { useContext, useEffect, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { depositFrequencyOptions } from '~/constants/depositFrequency';
import { DepositContext } from '~/context/Deposit/DepositContext';
import { LocalStorageHelper } from '~/helpers/StorageHelper';
import { useUpdateAllocationClassification } from '~/hooks/managed/useUpdateAllocationClassification';
import { useManagedPortfolioQuery } from '~/hooks/queries/portfolios/managed/useManagedPortfolioQuery';
import { useWhiskeyPortfolioQuery } from '~/hooks/queries/portfolios/whiskey/useWhiskeyPortfolioQuery';
import { DepositFrequency } from '~/models/deposits/DepositFrequency';
import { PortfolioTypes } from '~/models/PortfolioType';
import { GetUserPaymentMethodsResponse, PaymentMethod } from '~/networking';
import { baseRoutes } from '~/routes/base';
import { formatCurrency } from '~/utils/currencyFormatter';
import { getMinimumDepositAmount } from '~/utils/deposits/depositAmountUtils';
import { Button, LoadingSkeleton, Placeholder } from '~/vinovest-design-system/components';
import { Alert } from '~/vinovest-design-system/components/Form/components/Alert';

type Inputs = {
    frequency: string;
    amount?: number;
    source: string;
};

interface PriceFormData {
    amount: number | undefined;
    source: string;
    frequency: DepositFrequency;
    destination: PortfolioTypes;
}

interface AddFundsFormProps {
    savedPaymentMethod: GetUserPaymentMethodsResponse | undefined;
    onDepositInfoEntered: ({ frequency, amount, source }: Inputs) => void;
    onPaymentChanged: (updatedPayment: string) => void;
    loadedPaymentOptions: boolean;
}

/** Captures user's deposit amount and payment option information. */
export const AddFundsForm = ({
    savedPaymentMethod,
    onDepositInfoEntered,
    onPaymentChanged,
    loadedPaymentOptions
}: AddFundsFormProps) => {
    const {
        defaultPaymentSourceID,
        depositAmount,
        setDefaultPaymentSourceID,
        setMinDepositAmount,
        setDepositFrequency,
        destinationPortfolio,
        setDestinationPortfolio,
        allocationClassification,
        setAllocationClassification
    } = useContext(DepositContext);
    const { data: managedPortfolio } = useManagedPortfolioQuery();
    const { data: whiskeyPortfolio } = useWhiskeyPortfolioQuery();
    const { mutate } = useUpdateAllocationClassification();
    const [hasOpenedModal, setHasOpenedModal] = useState(true);

    const { handleSubmit, formState, control, setValue, watch } = useForm<PriceFormData>({
        mode: 'onChange',
        defaultValues: {
            amount: undefined,
            source: defaultPaymentSourceID,
            frequency: DepositFrequency.once,
            destination: destinationPortfolio
        }
    });
    const { errors, isValid } = formState;

    useEffect(() => {
        if (depositAmount != 0 && depositAmount != null) {
            setValue('amount', depositAmount, { shouldValidate: true });
        }
    }, [depositAmount]);

    useEffect(() => {
        if (managedPortfolioTotalCash > 0 || whiskeyPortfolioTotalCash > 0) {
            LocalStorageHelper.setItem('confirmed-acknowledgements', true);
        }
    });

    const watchPortfolioDestination = watch('destination');
    const watchDepositFrequency = watch('frequency');
    const watchDepositAmount = watch('amount');

    useEffect(() => {
        setDestinationPortfolio(watchPortfolioDestination);
    }, [watchPortfolioDestination]);

    useEffect(() => {
        setDepositFrequency(watchDepositFrequency);
    }, [watchDepositFrequency]);

    const hasSavedPaymentMethod = savedPaymentMethod?.paymentMethods && savedPaymentMethod?.paymentMethods.length > 0;

    const managedPortfolioTotalCash =
        (managedPortfolio?.cash?.amount || 0) + (managedPortfolio?.positionTotal?.amount || 0);

    const whiskeyPortfolioTotalCash =
        (whiskeyPortfolio?.cash?.amount || 0) + (whiskeyPortfolio?.positionTotal?.amount || 0);

    const minimumDepositAmount = getMinimumDepositAmount(
        watchPortfolioDestination,
        watchDepositFrequency,
        managedPortfolioTotalCash,
        whiskeyPortfolioTotalCash
    );

    useEffect(() => {
        if (loadedPaymentOptions && !hasSavedPaymentMethod && !hasOpenedModal) {
            setHasOpenedModal(true);
        }
    }, [loadedPaymentOptions, savedPaymentMethod, hasOpenedModal, setHasOpenedModal, hasSavedPaymentMethod]);

    useEffect(() => {
        if (defaultPaymentSourceID === 'new' && savedPaymentMethod && savedPaymentMethod.paymentMethods) {
            const methods = [...(savedPaymentMethod?.paymentMethods || [])];
            if (methods.length > 0) {
                setValue('source', methods[0].id);
                setDefaultPaymentSourceID(methods[0].id);
            }
        }
    }, [savedPaymentMethod, setDefaultPaymentSourceID, defaultPaymentSourceID, setValue]);

    const onSubmit: SubmitHandler<Inputs> = (data) => {
        if (allocationClassification != null && whiskeyPortfolio?.id != null) {
            mutate({ payload: { allocationClassification }, portfolioID: whiskeyPortfolio.id });
        }
        onDepositInfoEntered(data);
        setMinDepositAmount(minimumDepositAmount);
        setAllocationClassification(null);
        return data;
    };

    const amountValidationColor = errors?.amount ? 'text-red-600' : 'text-gray-400';
    const invalidManagedPortfolioDeposit =
        managedPortfolio?.code === 401 && watchPortfolioDestination === PortfolioTypes.managed;

    const insufficientManagedFundsAfterDeposit =
        watchPortfolioDestination === PortfolioTypes.managed &&
        watchDepositAmount &&
        managedPortfolio &&
        managedPortfolio?.cash?.amount &&
        managedPortfolio?.cash?.amount / 100 + watchDepositAmount <= 500;
    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} className={'h-full flex flex-col'}>
                <section className={'mb-2 flex items-center relative'}>
                    <label className={'uppercase text-10 text-neutrals-500 w-1/4'} htmlFor={'amount'}>
                        Amount
                    </label>
                    <Controller
                        control={control}
                        name={'amount'}
                        rules={{ required: 'Please enter a deposit amount.', min: minimumDepositAmount }}
                        render={({ field: { name, onChange, value, onBlur } }) => (
                            <CurrencyInput
                                prefix={'$'}
                                defaultValue={0}
                                id={name}
                                name={name}
                                decimalsLimit={2}
                                onValueChange={onChange}
                                className={'border-neutrals-200 border text-12 p-3 w-3/4 rounded'}
                                value={value}
                                onBlur={onBlur}
                                placeholder={'$0'}
                            />
                        )}
                    />
                    <span className={`text-10 absolute right-2 ${amountValidationColor}`}>
                        Min: {formatCurrency(minimumDepositAmount, false)}
                    </span>
                </section>
                <section className={'mb-2 flex items-center'}>
                    <label className={'uppercase text-10 text-neutrals-500 w-1/4'} htmlFor={'source'}>
                        From
                    </label>
                    <Placeholder
                        renderCondition={hasSavedPaymentMethod || loadedPaymentOptions}
                        placeholder={<LoadingSkeleton height={'40px'} width={'75%'} />}
                    >
                        <Controller
                            control={control}
                            name={'source'}
                            render={({ field: { name, onChange } }) => (
                                <select
                                    className={'border-neutrals-200 border text-12 p-3 w-3/4 rounded bg-white py-2'}
                                    style={{
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none'
                                    }}
                                    onChange={(e: { target: { value: string } }) => {
                                        onChange(e);
                                        onPaymentChanged(e.target.value);
                                    }}
                                    name={name}
                                    disabled={!loadedPaymentOptions}
                                >
                                    {savedPaymentMethod?.paymentMethods?.map((item: PaymentMethod) => (
                                        <option
                                            value={item.id}
                                            key={item.id}
                                            selected={defaultPaymentSourceID === item.id}
                                        >
                                            {item?.card != null
                                                ? item?.card.brand.toUpperCase()
                                                : item?.ach?.name.toUpperCase()}{' '}
                                            ****
                                            {item?.card != null ? item?.card?.lastFour : item?.ach?.lastFour}
                                        </option>
                                    ))}
                                    <option value={'new'}>Connect New Account</option>
                                </select>
                            )}
                        />
                    </Placeholder>
                </section>
                <section className={'mb-2 flex items-center'}>
                    <label className={'uppercase text-10 text-neutrals-500 w-1/4'} htmlFor={'destination'}>
                        To
                    </label>
                    <Controller
                        control={control}
                        name={'destination'}
                        render={({ field: { name, onChange } }) => (
                            <select
                                onChange={onChange}
                                name={name}
                                className={'border-neutrals-200 border text-12 p-3 w-3/4 rounded bg-white capitalize'}
                                style={{
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none'
                                }}
                                defaultValue={destinationPortfolio}
                            >
                                {Object.values(PortfolioTypes).map((portfolioType) => {
                                    return (
                                        <option
                                            key={`${portfolioType}-add-funds-option`}
                                            id={`${portfolioType}`}
                                            value={portfolioType}
                                        >
                                            {portfolioType == 'managed' && 'Wine (Managed)'}
                                            {portfolioType == 'whiskey' && 'Whiskey (Managed)'}
                                            {portfolioType == 'trading' && 'Trading'}
                                        </option>
                                    );
                                })}
                            </select>
                        )}
                    />
                </section>
                <section className={'flex items-center mb-6'}>
                    <label className={'uppercase text-10 text-neutrals-500 w-1/4'} htmlFor={'frequency'}>
                        Frequency
                    </label>
                    <Controller
                        control={control}
                        name={'frequency'}
                        render={({ field: { name, onChange } }) => (
                            <select
                                onChange={onChange}
                                name={name}
                                className={'border-neutrals-200 border text-12 p-3 w-3/4 rounded bg-white'}
                                style={{
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none'
                                }}
                            >
                                {Object.entries(depositFrequencyOptions).map(([key, value]) => (
                                    <option value={key} key={key}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                        )}
                    />
                </section>
                {invalidManagedPortfolioDeposit && (
                    <>
                        <Alert classnames={'mb-6 text-12 items-start'} type={'error'}>
                            In order to deposit into Managed, please finish setting up your Managed account.
                        </Alert>
                        <Button variant={'green'} classnames={'text-center mb-6'} href={baseRoutes.onboarding.href}>
                            Complete Profile
                        </Button>
                    </>
                )}

                {errors?.frequency?.message && (
                    <Alert classnames={'mb-6 text-12 items-start'} type={'error'}>
                        {errors?.frequency?.message}
                    </Alert>
                )}

                {errors?.source?.message && (
                    <Alert classnames={'mb-6 text-12 items-start'} type={'error'}>
                        {errors?.source?.message}
                    </Alert>
                )}
                {!!insufficientManagedFundsAfterDeposit && minimumDepositAmount === 0 && (
                    <Alert type={'warning'} classnames={'mb-6 text-12 items-start'}>
                        Most Wine cases cost at least $500. We recommend depositing enough funds in order to cover the
                        full cost of a case.
                    </Alert>
                )}

                <Button
                    variant={'green'}
                    type={'submit'}
                    disabled={!loadedPaymentOptions || !isValid || invalidManagedPortfolioDeposit}
                >
                    Review Transfer
                </Button>
            </form>
        </>
    );
};
